import debug from "debug";
const log = debug("app:eventHub");



/**
 * Singleton class for managing custom event listeners and emitting events.
 * Ensures that only one instance of EventHub exists across the application.
 * @category Utils
 * @subcategory EventManagement
 */
class EventHub {
  /**
   * Creates an instance of EventHub.
   * Initializes an empty object to store event listeners and prevents duplicate instances.
   */
  constructor() {
    if (EventHub.instance) {
      return EventHub.instance;
    }
    this.events = {};
    EventHub.instance = this;
  }

  cleanup() {
    console.log("Cleaning up EventHub: Removing all registered event listeners.");
    
    // Clear all stored event listeners
    this.events = {};
  
    console.log("EventHub cleanup complete.");
  }

  /**
   * Retrieves the singleton instance of EventHub.
   * @returns {EventHub} The singleton instance of EventHub.
   */
  static getInstance() {
    if (!EventHub.instance) {
      EventHub.instance = new EventHub();
    }
    return EventHub.instance;
  }

  /**
   * Registers a listener for a specific event.
   * @param {string} event - The name of the event to listen for.
   * @param {Function} listener - The callback function to execute when the event is emitted.
   * @param {number} [priority=0] - The priority of the listener, higher priority listeners are called first.
   */
  on(event, listener, priority = 0) {
    if (!this.events[event]) {
      this.events[event] = [];
    }

    // Prevent duplicate listeners
    if (this.events[event].some((item) => item.listener === listener)) {
      log(`Listener already added for event: ${event}`);

      return;
    }

    this.events[event].push({ listener, priority });
    this.events[event].sort((a, b) => b.priority - a.priority);
    this.listListeners();
  }

  /**
   * Registers a one-time listener for a specific event.
   * The listener is automatically removed after it is executed once.
   * @param {string} event - The name of the event to listen for.
   * @param {Function} listener - The callback function to execute when the event is emitted.
   * @param {number} [priority=0] - The priority of the listener, higher priority listeners are called first.
   */
  once(event, listener, priority = 0) {
    const onceWrapper = (...args) => {
      listener(...args);
      this.off(event, onceWrapper);
    };
    this.on(event, onceWrapper, priority);
  }

  /**
   * Removes a listener for a specific event.
   * @param {string} event - The name of the event to stop listening for.
   * @param {Function} listener - The callback function to remove.
   */
  off(event, listener) {
    if (!this.events[event]) {
      log(`No listeners found for event: ${event}`);
      return;
    }

    const idx = this.events[event].findIndex(
      (item) => item.listener === listener
    );
    if (idx !== -1) {
      this.events[event].splice(idx, 1);
    } else {
      log(`Listener not found for event: ${event}`);
    }
  }

  /**
   * Emits an event, triggering all registered listeners for that event.
   * Listeners are executed in order of their priority.
   * @param {string} event - The name of the event to emit.
   * @param {...any} args - Arguments to pass to the event listeners.
   */
  emit(event, ...args) {
    if (!this.events[event]) {
      log(`No listeners found for event: ${event}`);
      return;
    }

    const listeners = [...this.events[event]]; // Copy to prevent issues during event emission
    listeners.forEach((item) => {
      try {
        item.listener(...args);
      } catch (error) {
        console.error(`Error executing listener for event '${event}':`, error);
      }
    });
  }

  /**
   * Logs the current list of registered event listeners for debugging purposes.
   */
  listListeners() {
    log("Current event listeners:", this.events);
  }
}

export default EventHub;
